import { Checkbox, Modal } from 'antd';
import { useState, type FC } from 'react';
import { Input } from '../../../../components';
import { useGetSubjectsByBranch } from '../../../../services/subject';
import { type Filter } from '../../../../services/question';
import { useUser } from '../../../../hooks/useUser';
import { useGetTopics } from '../../../../services/topic';
import { IconSparkles, IconBook, IconFileText, IconX, IconClock } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTheme } from '../../../../contexts/ThemeContext';
import './GenerateTestModal.scss';

interface Props {
  onCancel: () => void
  onStart: (filters: Filter) => void
  visible: boolean
}

const GenerateTestModal: FC<Props> = ({ onCancel, onStart, visible }) => {
  const { user } = useUser();
  const { theme } = useTheme();
  const [numberOfQuestions, setNumberOfQuestions] = useState(30);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [bySubject, setBySubject] = useState(false);
  const [byTopic, setByTopic] = useState(false);
  const [pastExam, setPastExam] = useState(false);
  const [time, setTime] = useState(30);

  const { subjects, loading } = useGetSubjectsByBranch();
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  const { topics, loading: loadingTopics } = useGetTopics();
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const handleSelectSubject = (subject: string) => {
    setSelectedSubjects((prev) => {
      const included = prev.includes(subject);
      if (included) {
        return prev.filter((e) => e !== subject);
      }

      return [...prev, subject];
    });
  };

  const handleSelectTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const included = prev.includes(topic);
      if (included) {
        return prev.filter((e) => e !== topic);
      }

      return [...prev, topic];
    });
  };

  const handleStart = async () => {
    const u: { user: string } = {
      user: ''
    };
    if (onlyFailed || pastExam) {
      u.user = user?._id as string;
    }
    onStart({
      ...(u.user && u),
      numberOfQuestions,
      onlyFailed,
      pastExam,
      subjects: selectedSubjects,
      topics: selectedTopics,
      time
    });
  };

  return (
    <Modal
      title={
        <div className={clsx(
          'flex items-center gap-3',
          'text-gray-900 dark:text-white'
        )}>
          <div className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            'bg-gradient-to-br from-purple-500 to-pink-500',
            'dark:from-purple-600 dark:to-pink-600'
          )}>
            <IconSparkles size={20} className="text-white" />
          </div>
          <span className="text-xl font-bold">Generar test</span>
        </div>
      }
      open={visible}
      onOk={handleStart}
      onCancel={onCancel}
      okText="Empezar"
      destroyOnClose={true}
      maskClosable={true}
      okButtonProps={{
        className: clsx(
          'bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600',
          'border-none shadow-md hover:shadow-lg',
          'transition-all duration-300'
        )
      }}
      cancelButtonProps={{
        className: clsx(
          'border-gray-300 dark:border-gray-600',
          'text-gray-700 dark:text-gray-300',
          'hover:border-gray-400 dark:hover:border-gray-500',
          'transition-all duration-300'
        )
      }}
      width={700}
      className="generate-test-modal"
      style={{
        borderRadius: '16px',
        overflow: 'hidden'
      }}
      maskStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      wrapClassName={clsx(
        'generate-test-modal-wrapper',
        theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
        'bottom-sheet-mobile'
      )}
      centered={false}
    >
      <div className={clsx('space-y-4')}>
        {/* Input fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className={clsx(
              'text-sm font-medium transition-colors duration-300',
              'text-gray-700 dark:text-gray-300'
            )}>
              Número de preguntas
            </label>
            <Input
              placeholder='Número de preguntas'
              type='number'
              value={numberOfQuestions}
              onChange={(e) => setNumberOfQuestions(Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <label className={clsx(
              'text-sm font-medium transition-colors duration-300',
              'text-gray-700 dark:text-gray-300',
              'flex items-center gap-2'
            )}>
              <IconClock size={16} />
              Tiempo (minutos)
            </label>
            <Input
              placeholder='Tiempo (min)'
              type='number'
              value={time}
              onChange={(e) => setTime(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Filters section */}
        <div className={clsx(
          'space-y-4 pt-4 border-t transition-colors duration-300',
          'border-gray-200 dark:border-gray-700'
        )}>
          <h3 className={clsx(
            'text-base font-semibold mb-3 transition-colors duration-300',
            'text-gray-900 dark:text-white'
          )}>
            Filtros
          </h3>
          {/* Select subjects */}
          <div className="space-y-2">
            <Checkbox
              disabled={loading}
              checked={bySubject}
              onChange={(e) => setBySubject(e.target.checked)}
              className={clsx(
                'text-gray-900 dark:text-gray-100',
                '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
              )}
            >
              <span className="flex items-center gap-2">
                <IconBook size={16} />
                <span className="font-medium">Seleccionar bloques</span>
              </span>
            </Checkbox>
            {bySubject && (
              <div className={clsx(
                'ml-8 mt-2 p-4 rounded-lg transition-colors duration-300',
                'bg-purple-50 dark:bg-purple-900/20',
                'border border-purple-200 dark:border-purple-800'
              )}>
                <div className="flex flex-wrap gap-3">
                  {subjects?.map((subject) => (
                    <Checkbox
                      key={subject._id}
                      checked={selectedSubjects.includes(subject._id!)}
                      onChange={() => handleSelectSubject(subject._id!)}
                      className={clsx(
                        'text-gray-700 dark:text-gray-300',
                        '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
                      )}
                    >
                      {subject.name}
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Select topics */}
          <div className="space-y-2">
            <Checkbox
              disabled={loadingTopics}
              checked={byTopic}
              onChange={(e) => setByTopic(e.target.checked)}
              className={clsx(
                'text-gray-900 dark:text-gray-100',
                '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
              )}
            >
              <span className="flex items-center gap-2">
                <IconFileText size={16} />
                <span className="font-medium">Seleccionar temas</span>
              </span>
            </Checkbox>
            {byTopic && (
              <div className={clsx(
                'ml-8 mt-2 p-4 rounded-lg transition-colors duration-300',
                'bg-indigo-50 dark:bg-indigo-900/20',
                'border border-indigo-200 dark:border-indigo-800'
              )}>
                <div className="flex flex-wrap gap-3">
                  {topics?.map((topic) => (
                    <Checkbox
                      key={topic._id}
                      checked={selectedTopics.includes(topic._id!)}
                      onChange={() => handleSelectTopic(topic._id!)}
                      className={clsx(
                        'text-gray-700 dark:text-gray-300',
                        '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
                      )}
                    >
                      {topic.name}
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Additional options */}
          <div className="space-y-3 pt-2">
            <Checkbox
              checked={onlyFailed}
              onChange={(e) => setOnlyFailed(e.target.checked)}
              className={clsx(
                'text-gray-900 dark:text-gray-100',
                '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
              )}
            >
              <span className="flex items-center gap-2">
                <IconX size={16} className="text-red-500" />
                <span className="font-medium">Usar solo preguntas falladas</span>
              </span>
            </Checkbox>
            <Checkbox
              checked={pastExam}
              onChange={(e) => setPastExam(e.target.checked)}
              className={clsx(
                'text-gray-900 dark:text-gray-100',
                '[&_.ant-checkbox]:transition-all [&_.ant-checkbox]:duration-300'
              )}
            >
              <span className="font-medium">Preguntas de oposiciones pasadas</span>
            </Checkbox>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { GenerateTestModal };
